import { FC, useState, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import type { Sampling, ParameterDefinition, LaboratoryArea } from '../../../api/complementApi';
import { Colors } from '../../../assets/GlobalStyle/Variables';
import {
  TrendsLayout,
  TrendsLeft,
  TrendsRight,
  TrendsActionsBar,
  AreaHeader,
  ExpandIcon,
  AreaContent,
  ParameterSection,
  ParameterHeader,
  ParameterInfo,
  MiniTable,
  MiniTableHeader,
  MiniTableRow,
  MiniTableCell,
  ChartContainer,
  NoChartsMessage,
  SecondaryButton,
  SmallButton,
} from './styles';

type ParameterEntry = {
  name: string;
  abbreviation: string;
  units: string;
  results: Array<{
    id: number;
    made_at: string;
    sampling_id: number;
    value: string | null;
    is_out_of_range: string;
    parameter_definition: number | null;
  }>;
  limits: { lower: number | null; upper: number | null };
};

type AreasMap = Record<string, Record<string, ParameterEntry>>;

type Props = {
  samplings: Sampling[];
  parameterDefinitions: ParameterDefinition[];
  laboratoryAreas: LaboratoryArea[];
  onCopy: (results: Sampling['parameter_results']) => void;
};

const isOutOfRange = (val: string | null | undefined) =>
  val === 'true' || (val as unknown) === true;

export const LabTrends: FC<Props> = ({ samplings, parameterDefinitions, onCopy }) => {
  const [expandedAreas, setExpandedAreas] = useState<Record<string, boolean>>({});
  const [selectedParams, setSelectedParams] = useState<Record<string, boolean>>({});

  const paramDefsMap = useMemo(() => {
    const map: Record<number, ParameterDefinition> = {};
    parameterDefinitions.forEach((pd) => { map[pd.id] = pd; });
    return map;
  }, [parameterDefinitions]);

  const areas: AreasMap = useMemo(() => {
    const result: AreasMap = {};

    samplings.forEach((sampling) => {
      sampling.parameter_results?.forEach((r) => {
        let areaName = 'Ostatní';
        let lower: number | null = null;
        let upper: number | null = null;

        if (r.parameter_definition && paramDefsMap[r.parameter_definition]) {
          const pd = paramDefsMap[r.parameter_definition];
          areaName = pd.area_name || 'Ostatní';
          lower = pd.lower_limit ? parseFloat(pd.lower_limit) : null;
          upper = pd.upper_limit ? parseFloat(pd.upper_limit) : null;
        } else {
          // fallback from effective limits on result
          lower = r.effective_lower_limit ? parseFloat(r.effective_lower_limit) : null;
          upper = r.effective_upper_limit ? parseFloat(r.effective_upper_limit) : null;
        }

        if (!result[areaName]) result[areaName] = {};

        const paramKey = `${r.parameter_name}_${r.parameter_abbreviation}`;
        if (!result[areaName][paramKey]) {
          result[areaName][paramKey] = {
            name: r.parameter_name,
            abbreviation: r.parameter_abbreviation,
            units: r.parameter_units,
            results: [],
            limits: { lower, upper },
          };
        }

        result[areaName][paramKey].results.push({
          id: r.id,
          made_at: sampling.made_at,
          sampling_id: sampling.id,
          value: r.value,
          is_out_of_range: r.is_out_of_range,
          parameter_definition: r.parameter_definition,
        });
      });
    });

    Object.values(result).forEach((params) => {
      Object.values(params).forEach((p) => {
        p.results.sort((a, b) => new Date(b.made_at).getTime() - new Date(a.made_at).getTime());
      });
    });

    return result;
  }, [samplings, paramDefsMap]);

  const toggleArea = (areaName: string) => {
    setExpandedAreas((prev) => ({ ...prev, [areaName]: !prev[areaName] }));
  };

  const toggleParam = (areaName: string, paramKey: string) => {
    const key = `${areaName}|||${paramKey}`;
    setSelectedParams((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const clearAll = () => setSelectedParams({});

  const hasSelected = Object.values(selectedParams).some(Boolean);

  const selectedCharts = useMemo(() => {
    return Object.entries(selectedParams)
      .filter(([, v]) => v)
      .map(([key]) => {
        const [areaName, paramKey] = key.split('|||');
        const parameter = areas[areaName]?.[paramKey];
        return parameter ? { areaName, paramKey, parameter, key } : null;
      })
      .filter(Boolean) as Array<{ areaName: string; paramKey: string; parameter: ParameterEntry; key: string }>;
  }, [selectedParams, areas]);

  const handleCopyParam = (parameter: ParameterEntry) => {
    const fakeResults = parameter.results.map((r) => ({
      id: r.id,
      is_out_of_range: r.is_out_of_range,
      parameter_name: parameter.name,
      parameter_abbreviation: parameter.abbreviation,
      parameter_units: parameter.units,
      result_type: 'QUANTITATIVE' as const,
      effective_lower_limit: null,
      effective_upper_limit: null,
      external_id: null,
      value: r.value,
      text_value: null,
      rich_text_value: null,
      comment_value: null,
      sampling: r.sampling_id,
      parameter_definition: r.parameter_definition,
    }));
    onCopy(fakeResults);
  };

  if (samplings.length === 0) {
    return <NoChartsMessage>Žádné výsledky pro zobrazení trendů</NoChartsMessage>;
  }

  return (
    <>
      {hasSelected && (
        <TrendsActionsBar>
          <SecondaryButton type="button" onClick={clearAll}>
            Zrušit výběr všech
          </SecondaryButton>
        </TrendsActionsBar>
      )}

      <TrendsLayout>
        <TrendsLeft>
          {Object.entries(areas).map(([areaName, params]) => (
            <div key={areaName}>
              <AreaHeader onClick={() => toggleArea(areaName)}>
                <span>{areaName}</span>
                <ExpandIcon $expanded={expandedAreas[areaName]}>▼</ExpandIcon>
              </AreaHeader>

              {expandedAreas[areaName] && (
                <AreaContent>
                  {Object.entries(params).map(([paramKey, parameter]) => {
                    const selKey = `${areaName}|||${paramKey}`;
                    const isSelected = selectedParams[selKey] ?? false;
                    return (
                      <ParameterSection key={paramKey}>
                        <ParameterHeader>
                          <ParameterInfo>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleParam(areaName, paramKey)}
                            />
                            <span>
                              {parameter.name}
                              {parameter.abbreviation && ` (${parameter.abbreviation})`}
                            </span>
                          </ParameterInfo>
                          <SmallButton type="button" onClick={() => handleCopyParam(parameter)}>
                            Kopírovat
                          </SmallButton>
                        </ParameterHeader>

                        <MiniTable>
                          <MiniTableHeader>
                            <MiniTableCell>Datum</MiniTableCell>
                            <MiniTableCell>Hodnota</MiniTableCell>
                            <MiniTableCell>{parameter.units || '–'}</MiniTableCell>
                          </MiniTableHeader>
                          {parameter.results.slice(0, 10).map((r, i) => (
                            <MiniTableRow
                              key={`${r.sampling_id}_${r.id}_${i}`}
                              $outOfRange={isOutOfRange(r.is_out_of_range)}
                            >
                              <MiniTableCell>
                                {new Date(r.made_at).toLocaleDateString('cs-CZ')}
                              </MiniTableCell>
                              <MiniTableCell>{r.value ?? '–'}</MiniTableCell>
                              <MiniTableCell>{parameter.units}</MiniTableCell>
                            </MiniTableRow>
                          ))}
                        </MiniTable>
                      </ParameterSection>
                    );
                  })}
                </AreaContent>
              )}
            </div>
          ))}
        </TrendsLeft>

        <TrendsRight>
          {selectedCharts.length > 0 ? (
            selectedCharts.map(({ parameter, key }) => {
              const chartData = parameter.results
                .slice()
                .reverse()
                .map((r) => ({
                  date: new Date(r.made_at).toLocaleDateString('cs-CZ'),
                  value: parseFloat(r.value ?? '') || 0,
                  isOutOfRange: isOutOfRange(r.is_out_of_range),
                }));

              return (
                <ChartContainer key={key}>
                  <h4>
                    {parameter.name}
                    {parameter.abbreviation && ` (${parameter.abbreviation})`}
                    {parameter.units && ` [${parameter.units}]`}
                  </h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart
                      data={chartData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11 }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis
                        tick={{ fontSize: 11 }}
                        label={{
                          value: parameter.units,
                          angle: -90,
                          position: 'insideLeft',
                          style: { fontSize: 11 },
                        }}
                      />
                      <Tooltip
                        formatter={(value) => [
                          `${Number(value)} ${parameter.units}`,
                          parameter.name,
                        ]}
                        labelFormatter={(label) => `Datum: ${label}`}
                      />
                      {parameter.limits.lower !== null && (
                        <ReferenceLine
                          y={parameter.limits.lower}
                          stroke={Colors.orange}
                          strokeDasharray="6 3"
                          label={{
                            value: `min: ${parameter.limits.lower}`,
                            position: 'insideTopLeft',
                            fontSize: 10,
                            fill: Colors.orange,
                          }}
                        />
                      )}
                      {parameter.limits.upper !== null && (
                        <ReferenceLine
                          y={parameter.limits.upper}
                          stroke={Colors.orange}
                          strokeDasharray="6 3"
                          label={{
                            value: `max: ${parameter.limits.upper}`,
                            position: 'insideBottomLeft',
                            fontSize: 10,
                            fill: Colors.orange,
                          }}
                        />
                      )}
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#2563eb"
                        strokeWidth={2}
                        dot={(props: any) => {
                          const { cx, cy, payload } = props;
                          const color = payload.isOutOfRange ? Colors.error : '#2563eb';
                          return (
                            <circle
                              key={`dot-${cx}-${cy}`}
                              cx={cx}
                              cy={cy}
                              r={4}
                              fill={color}
                              stroke={color}
                              strokeWidth={2}
                            />
                          );
                        }}
                        activeDot={{ r: 6, fill: '#1d4ed8' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartContainer>
              );
            })
          ) : (
            <NoChartsMessage>Vyberte parametry v levé části pro zobrazení grafů</NoChartsMessage>
          )}
        </TrendsRight>
      </TrendsLayout>
    </>
  );
};
